/* TourStock flight case label designer.
 * Draws a low resolution, watermarked proof in the browser (rendering comes
 * from ts-label-core.js), sends the proof and the customer's logo to Shopify
 * Files through the Growth OS storefront endpoints, then adds the pack (and the
 * artwork setup fee) to the basket. The print ready PDF is made on the server
 * after the order is paid. Prices come from Shopify only. */
(function () {
  "use strict";

  var root = document.getElementById("tlb-root");
  var cfgNode = document.querySelector("[data-tlb-config]");
  if (!root || !cfgNode || !window.fetch || !window.Promise) return;
  var cfg;
  try {
    cfg = JSON.parse(cfgNode.textContent);
  } catch (e) {
    return;
  }

  var core = window.TLBCore;
  if (!core) return;
  var W = core.W;
  var H = core.H;
  var FONTS = core.FONTS;
  var GHOST_HEADING = core.GHOST_HEADING;
  var GHOST_SUB = core.GHOST_SUB;
  var clamp = core.clamp;
  var rgb = core.rgb;
  var lum = core.lum;
  var rgba = core.rgba;
  var contrast = core.contrast;

  /* Deliberately low: about 76 dpi, watermarked. The print file is made on the
     server after an order, so a customer cannot take the artwork elsewhere. */
  var PREVIEW_PPM = 3;
  var LOGO_PPM = 300 / 25.4;
  var MAX_FIELDS = 12;
  var STORE_KEY = "tlb-design-v1";
  var LOGO_KEY = "tlb-logo-v1";
  var LOGO_STORE_MAX = 1400000;

  var LAYOUTS = [
    { id: "banner", name: "Banner" },
    { id: "classic", name: "Classic" },
    { id: "sidebar", name: "Side bar" },
    { id: "centred", name: "Centred" },
  ];
  var STOCK = [
    { hex: "#ffffff", name: "White" },
    { hex: "#9df01b", name: "Fluorescent green" },
    { hex: "#ff8a1f", name: "Fluorescent orange" },
    { hex: "#ff62a8", name: "Fluorescent pink" },
    { hex: "#f4f23a", name: "Fluorescent yellow" },
    { hex: "#3fc4ff", name: "Fluorescent blue" },
  ];
  var COLOURS = [
    {
      key: "accent",
      label: "Accent colour",
      swatches: ["#111111", "#e9ad14", "#d62828", "#1d4ed8", "#0e9f6e", "#7c3aed", "#f97316", "#64748b"],
      help: "Banners, bars and rules. Each extra colour is another colour to print.",
    },
    {
      key: "text",
      label: "Text colour",
      swatches: ["#111111", "#1d4ed8", "#d62828", "#0e9f6e", "#64748b"],
    },
    {
      key: "bg",
      label: "Label colour",
      stock: true,
      swatches: STOCK.map(function (s) { return s.hex; }),
      help: "PAL labels are made in white and a set of fluorescent colours, so the whole label colour is a choice of stock rather than a print colour. Fluorescent tones vary from the screen.",
    },
  ];
  var SPANS = [
    [2, "Narrow (a sixth)"],
    [3, "Quarter"],
    [4, "Third"],
    [6, "Half"],
    [8, "Two thirds"],
    [9, "Three quarters"],
    [12, "Full width"],
  ];
  var SPAN_VALUES = SPANS.map(function (s) { return s[0]; });
  var HEIGHTS = [
    [3, "Short"],
    [0, "Standard"],
    [1, "Taller"],
    [2, "Tall"],
  ];
  var CHIPS = ["Venue", "Department", "Weight (kg)", "Truck / Load", "Show date", "Fragile", "This way up"];

  var uidCounter = 0;
  function uid() {
    uidCounter += 1;
    return "f" + uidCounter + Math.random().toString(36).slice(2, 6);
  }

  function defaultFields() {
    return [
      { uid: uid(), caption: "Production", span: 6, h: 0, on: true },
      { uid: uid(), caption: "Job No.", span: 3, h: 0, on: true },
      { uid: uid(), caption: "Case No.", span: 3, h: 0, on: true },
      { uid: uid(), caption: "Date Out", span: 4, h: 0, on: true },
      { uid: uid(), caption: "Date In", span: 4, h: 0, on: true },
      { uid: uid(), caption: "Weight (kg)", span: 4, h: 0, on: true },
      { uid: uid(), caption: "Contents", span: 12, h: 1, on: true },
    ];
  }

  /* Starting points. The event and stock sets follow the box wording on the
     PAL label proofs from the printer. */
  var PRESETS = [
    {
      id: "touring",
      name: "Touring flight cases",
      hint: "Production, job and case numbers, dates, weight, contents",
      fields: function () {
        return defaultFields();
      },
    },
    {
      id: "event",
      name: "Event or show",
      hint: "Client or event, job number, venue, event date, notes",
      fields: function () {
        return [
          { uid: uid(), caption: "Client / Event", span: 6, h: 0, on: true },
          { uid: uid(), caption: "Job No.", span: 3, h: 0, on: true },
          { uid: uid(), caption: "Event Date", span: 3, h: 0, on: true },
          { uid: uid(), caption: "Venue", span: 12, h: 0, on: true },
          { uid: uid(), caption: "Contents / Notes", span: 12, h: 1, on: true },
        ];
      },
    },
    {
      id: "stock",
      name: "Stock and warehouse",
      hint: "Product, bin number, checked by, contents and quantity",
      fields: function () {
        return [
          { uid: uid(), caption: "Product", span: 6, h: 0, on: true },
          { uid: uid(), caption: "Bin No.", span: 3, h: 0, on: true },
          { uid: uid(), caption: "Checked By", span: 3, h: 0, on: true },
          { uid: uid(), caption: "Contents / Quantity / Job", span: 12, h: 1, on: true },
        ];
      },
    },
  ];

  function newDesignId() {
    var s = "";
    while (s.length < 14) s += Math.random().toString(36).slice(2);
    return s.slice(0, 14);
  }

  var S = {
    mode: "design",
    layout: "banner",
    heading: "",
    sub: "",
    caps: true,
    font: "condensed",
    accent: "#111111",
    text: "#111111",
    bg: "#ffffff",
    logo: null,
    logoSide: "left",
    logoScale: 85,
    logoPanel: true,
    logoFree: null,
    headH: null,
    sideW: null,
    titleScale: null,
    align: null,
    lineW: 1,
    fields: defaultFields(),
    guides: false,
    reorder: null,
    notes: "",
    ownFiles: [],
    designId: newDesignId(),
  };

  var ui = { sel: null, drop: null, stash: null };
  var R = core.create(S);
  var drawLabel = R.drawLabel;
  var geometry = R.geometry;
  var logoPlacement = R.logoPlacement;
  var freePlacement = R.freePlacement;
  var inspect = R.inspect;

  function q(sel, scope) {
    return (scope || root).querySelector(sel);
  }
  function qa(sel, scope) {
    return Array.prototype.slice.call((scope || root).querySelectorAll(sel));
  }

  /* ----------------------------------------------------------- persistence */

  var HEX = /^#[0-9a-f]{6}$/i;
  function restore() {
    var raw;
    try {
      raw = JSON.parse(window.localStorage.getItem(STORE_KEY) || "null");
    } catch (e) {
      raw = null;
    }
    applyRaw(raw);
  }

  function applyRaw(raw) {
    if (!raw || typeof raw !== "object") return;
    if (LAYOUTS.some(function (l) { return l.id === raw.layout; })) S.layout = raw.layout;
    if (typeof raw.heading === "string") S.heading = raw.heading.slice(0, 40);
    if (typeof raw.sub === "string") S.sub = raw.sub.slice(0, 60);
    if (typeof raw.caps === "boolean") S.caps = raw.caps;
    if (FONTS[raw.font]) S.font = raw.font;
    ["accent", "text"].forEach(function (k) {
      if (typeof raw[k] === "string" && HEX.test(raw[k])) S[k] = raw[k];
    });
    if (typeof raw.bg === "string") {
      var stock = STOCK.filter(function (st) { return st.hex === raw.bg.toLowerCase(); })[0];
      S.bg = stock ? stock.hex : "#ffffff";
    }
    if (["left", "centre", "right"].indexOf(raw.logoSide) >= 0) S.logoSide = raw.logoSide;
    if (typeof raw.logoScale === "number") S.logoScale = Math.min(100, Math.max(40, raw.logoScale));
    if (typeof raw.logoPanel === "boolean") S.logoPanel = raw.logoPanel;
    var lf = raw.logoFree;
    S.logoFree =
      lf && typeof lf.x === "number" && typeof lf.y === "number" && typeof lf.w === "number"
        ? { x: clamp(lf.x, 0, W), y: clamp(lf.y, 0, H), w: clamp(lf.w, 10, 180) }
        : null;
    S.headH = typeof raw.headH === "number" ? clamp(raw.headH, 20, 100) : null;
    S.sideW = typeof raw.sideW === "number" ? clamp(raw.sideW, 12, 44) : null;
    S.titleScale = typeof raw.titleScale === "number" ? clamp(raw.titleScale, 50, 140) : null;
    S.align = ["left", "centre", "right"].indexOf(raw.align) >= 0 ? raw.align : null;
    S.lineW = [0, 1, 2].indexOf(raw.lineW) >= 0 ? raw.lineW : 1;
    if (Array.isArray(raw.fields)) {
      var fields = raw.fields
        .slice(0, MAX_FIELDS)
        .filter(function (f) { return f && typeof f.caption === "string"; })
        .map(function (f) {
          return {
            uid: uid(),
            caption: f.caption.slice(0, 24),
            span: SPAN_VALUES.indexOf(f.span) >= 0 ? f.span : 4,
            h: typeof f.h === "number" ? clamp(Math.round(f.h), 0, 3) : f.tall ? 1 : 0,
            on: f.on !== false,
          };
        });
      if (fields.length) S.fields = fields;
    }
    ui.stash = null;
  }

  function snapshotRaw() {
    return {
      layout: S.layout,
      heading: S.heading,
      sub: S.sub,
      caps: S.caps,
      font: S.font,
      accent: S.accent,
      text: S.text,
      bg: S.bg,
      logoSide: S.logoSide,
      logoScale: S.logoScale,
      logoPanel: S.logoPanel,
      logoFree: S.logoFree,
      headH: S.headH,
      sideW: S.sideW,
      titleScale: S.titleScale,
      align: S.align,
      lineW: S.lineW,
      fields: S.fields.map(function (f) {
        return { caption: f.caption, span: f.span, h: f.h, on: f.on };
      }),
    };
  }

  var persistedLogo = null;

  function persistLogo() {
    var logo = S.logo;
    persistedLogo = logo;
    try {
      if (!logo || !logo.file || logo.file.size > LOGO_STORE_MAX || !window.FileReader) {
        window.localStorage.removeItem(LOGO_KEY);
        return;
      }
      var reader = new FileReader();
      reader.onload = function () {
        if (S.logo !== logo || typeof reader.result !== "string") return;
        try {
          window.localStorage.setItem(
            LOGO_KEY,
            JSON.stringify({ name: logo.name, type: logo.file.type || "", data: reader.result }),
          );
        } catch (e) {
          /* storage full: the logo is simply not remembered */
        }
      };
      reader.readAsDataURL(logo.file);
    } catch (e) {
      /* private mode */
    }
  }

  function restoreLogo() {
    var stored;
    try {
      stored = JSON.parse(window.localStorage.getItem(LOGO_KEY) || "null");
    } catch (e) {
      stored = null;
    }
    if (!stored || typeof stored.data !== "string" || typeof stored.name !== "string") return;
    persistedLogo = null;
    fetch(stored.data)
      .then(function (res) { return res.blob(); })
      .then(function (blob) {
        loadLogo(new File([blob], stored.name, { type: stored.type || blob.type || "" }), true);
      })
      .catch(function () {
        /* the remembered logo could not be read: start without it */
      });
  }

  function save() {
    var json = JSON.stringify(snapshotRaw());
    try {
      window.localStorage.setItem(STORE_KEY, json);
    } catch (e) {
      /* private mode: the design simply is not remembered */
    }
    if (S.logo !== persistedLogo) persistLogo();
    track(json);
  }

  /* Undo and redo. Changes that arrive close together (typing, dragging a
     slider or a grip) are one step. */

  var hist = { undo: [], redo: [], cur: null, at: 0 };
  var HIST_GAP = 800;
  var HIST_MAX = 60;

  function track(json) {
    if (!hist.cur) {
      hist.cur = { json: json, logo: S.logo };
      return;
    }
    if (hist.cur.json === json && hist.cur.logo === S.logo) return;
    var now = Date.now();
    if (now - hist.at > HIST_GAP) {
      hist.undo.push(hist.cur);
      if (hist.undo.length > HIST_MAX) hist.undo.shift();
    }
    hist.cur = { json: json, logo: S.logo };
    hist.at = now;
    hist.redo = [];
  }

  function restoreSnap(snap) {
    applyRaw(JSON.parse(snap.json));
    S.logo = snap.logo;
    if (!S.logo) S.logoFree = null;
    hist.cur = snap;
    hist.at = 0;
    ui.sel = null;
    syncControls();
    schedule();
  }

  function flush() {
    if (!frame) return;
    window.cancelAnimationFrame(frame);
    frame = 0;
    paint();
  }

  function undo() {
    flush();
    if (!hist.undo.length) return;
    hist.redo.push(hist.cur);
    restoreSnap(hist.undo.pop());
  }

  function redo() {
    flush();
    if (!hist.redo.length) return;
    hist.undo.push(hist.cur);
    restoreSnap(hist.redo.pop());
  }

  function startAgain() {
    track(JSON.stringify(snapshotRaw()));
    hist.at = 0;
    applyRaw({
      layout: "banner",
      heading: "",
      sub: "",
      caps: true,
      font: "condensed",
      accent: "#111111",
      text: "#111111",
      bg: "#ffffff",
      logoSide: "left",
      logoScale: 85,
      logoPanel: true,
      logoFree: null,
      fields: defaultFields(),
    });
    S.logo = null;
    syncControls();
    schedule();
  }

  /* ------------------------------------------------------------------- UI */

  var canvas = q("#tlb-canvas");
  var ctxMain = canvas ? canvas.getContext("2d") : null;
  var thumbs = {};
  var frame = 0;

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(function () {
      frame = 0;
      paint();
    });
  }

  function paint() {
    if (S.mode === "design" && ctxMain) {
      drawLabel(ctxMain, PREVIEW_PPM, {
        ghost: true,
        guides: S.guides,
        handles: !!S.logoFree,
        watermark: true,
        sizeHandles: true,
        selectUid: ui.sel,
        dropUid: ui.drop,
      });
      LAYOUTS.forEach(function (l) {
        var t = thumbs[l.id];
        if (t) drawLabel(t.getContext("2d"), t.width / W, { ghost: true, layout: l.id });
      });
    }
    syncLogoUi();
    syncSizes();
    syncFit();
    syncInks();
    syncPresets();
    updateSummary();
    save();
    syncHistoryUi();
  }

  function presetMatches(preset) {
    var want = preset.fields();
    if (want.length !== S.fields.length) return false;
    return want.every(function (w, i) {
      var f = S.fields[i];
      return f.caption === w.caption && f.span === w.span && f.h === w.h && f.on === w.on;
    });
  }

  function buildPresets() {
    var wrap = q("[data-tlb-presets]");
    if (!wrap) return;
    PRESETS.forEach(function (preset) {
      var label = document.createElement("label");
      label.className = "tlb-preset";
      var input = document.createElement("input");
      input.type = "radio";
      input.name = "tlb-preset";
      input.value = preset.id;
      var body = document.createElement("span");
      body.className = "tlb-preset__body";
      var name = document.createElement("strong");
      name.textContent = preset.name;
      var hint = document.createElement("small");
      hint.textContent = preset.hint;
      body.appendChild(name);
      body.appendChild(hint);
      label.appendChild(input);
      label.appendChild(body);
      wrap.appendChild(label);
      input.addEventListener("change", function () {
        if (!input.checked) return;
        S.fields = preset.fields();
        renderFields();
        schedule();
      });
    });
  }

  function syncPresets() {
    qa('input[name="tlb-preset"]').forEach(function (r) {
      var preset = PRESETS.filter(function (p) { return p.id === r.value; })[0];
      r.checked = !!preset && presetMatches(preset);
    });
    var count = q("[data-tlb-box-count]");
    if (count) {
      var on = S.fields.filter(function (f) { return f.on; }).length;
      count.textContent = on + (on === 1 ? " box" : " boxes");
    }
  }

  function buildLayouts() {
    var wrap = q("[data-tlb-layouts]");
    LAYOUTS.forEach(function (l) {
      var label = document.createElement("label");
      label.className = "tlb-layout";
      var input = document.createElement("input");
      input.type = "radio";
      input.name = "tlb-layout";
      input.value = l.id;
      input.checked = S.layout === l.id;
      var body = document.createElement("span");
      body.className = "tlb-layout__body";
      var c = document.createElement("canvas");
      c.width = 315;
      c.height = 225;
      c.setAttribute("aria-hidden", "true");
      thumbs[l.id] = c;
      var name = document.createElement("span");
      name.className = "tlb-layout__name";
      name.textContent = l.name;
      body.appendChild(c);
      body.appendChild(name);
      label.appendChild(input);
      label.appendChild(body);
      wrap.appendChild(label);
      input.addEventListener("change", function () {
        if (input.checked) {
          S.layout = l.id;
          S.headH = null;
          schedule();
        }
      });
    });
  }

  function buildFonts() {
    var wrap = q("[data-tlb-fonts]");
    Object.keys(FONTS).forEach(function (key) {
      var f = FONTS[key];
      var label = document.createElement("label");
      label.className = "tlb-font";
      var input = document.createElement("input");
      input.type = "radio";
      input.name = "tlb-font";
      input.value = key;
      input.checked = S.font === key;
      var body = document.createElement("span");
      body.className = "tlb-font__body";
      var sample = document.createElement("span");
      sample.className = "tlb-font__sample";
      sample.style.fontFamily = f.family;
      sample.style.fontWeight = String(f.weight);
      sample.textContent = "Aa 123";
      var name = document.createElement("span");
      name.className = "tlb-font__name";
      name.textContent = f.name;
      body.appendChild(sample);
      body.appendChild(name);
      label.appendChild(input);
      label.appendChild(body);
      wrap.appendChild(label);
      input.addEventListener("change", function () {
        if (input.checked) {
          S.font = key;
          loadFonts().then(schedule);
          schedule();
        }
      });
    });
  }

  var colourControls = {};
  function buildColours() {
    var wrap = q("[data-tlb-colours]");
    COLOURS.forEach(function (c) {
      var group = document.createElement("div");
      group.className = "tlb-colour";
      var head = document.createElement("div");
      head.className = "tlb-colour__head";
      var title = document.createElement("span");
      title.className = "tlb-label";
      title.style.margin = "0";
      title.textContent = c.label;
      head.appendChild(title);
      var swatches = document.createElement("div");
      swatches.className = "tlb-colour__swatches";
      var buttons = c.swatches.map(function (hex) {
        var b = document.createElement("button");
        b.type = "button";
        b.className = "tlb-swatch";
        b.style.background = hex;
        var stockItem = c.stock ? STOCK.filter(function (x) { return x.hex === hex; })[0] : null;
        b.setAttribute("aria-label", c.label + " " + (stockItem ? stockItem.name : hex));
        if (stockItem) b.title = stockItem.name;
        b.addEventListener("click", function () {
          S[c.key] = hex;
          syncColours();
          schedule();
        });
        swatches.appendChild(b);
        return { el: b, hex: hex };
      });
      var picker = null;
      if (!c.stock) {
        picker = document.createElement("input");
        picker.type = "color";
        picker.setAttribute("aria-label", c.label + " custom colour");
        picker.addEventListener("input", function () {
          S[c.key] = picker.value;
          syncColours();
          schedule();
        });
        swatches.appendChild(picker);
      }
      group.appendChild(head);
      group.appendChild(swatches);
      if (c.help) {
        var help = document.createElement("p");
        help.className = "tlb-help";
        help.textContent = c.help;
        group.appendChild(help);
      }
      wrap.appendChild(group);
      colourControls[c.key] = { buttons: buttons, picker: picker };
    });
  }

  function syncColours() {
    COLOURS.forEach(function (c) {
      var ctl = colourControls[c.key];
      if (ctl.picker) ctl.picker.value = S[c.key];
      ctl.buttons.forEach(function (b) {
        b.el.setAttribute("aria-pressed", b.hex.toLowerCase() === S[c.key].toLowerCase() ? "true" : "false");
      });
    });
  }

  /* Boxes to write in */

  var fieldList = q("[data-tlb-fieldlist]");

  function renderFields(focus) {
    fieldList.innerHTML = "";
    S.fields.forEach(function (f, i) {
      var li = document.createElement("li");
      li.className = "tlb-fieldrow" + (f.on ? "" : " is-off");
      li.setAttribute("data-uid", f.uid);

      var on = document.createElement("input");
      on.type = "checkbox";
      on.className = "tlb-fieldrow__on";
      on.checked = f.on;
      on.setAttribute("data-act", "on");
      on.setAttribute("aria-label", "Show this box on the label");

      var cap = document.createElement("input");
      cap.type = "text";
      cap.value = f.caption;
      cap.maxLength = 24;
      cap.placeholder = "Box label";
      cap.setAttribute("data-act", "caption");
      cap.setAttribute("aria-label", "Box label " + (i + 1));

      var rest = document.createElement("div");
      rest.className = "tlb-fieldrow__rest";
      var sel = document.createElement("select");
      sel.setAttribute("data-act", "span");
      sel.setAttribute("aria-label", "Box width");
      SPANS.forEach(function (s) {
        var o = document.createElement("option");
        o.value = String(s[0]);
        o.textContent = s[1];
        if (s[0] === f.span) o.selected = true;
        sel.appendChild(o);
      });
      var hsel = document.createElement("select");
      hsel.setAttribute("data-act", "height");
      hsel.setAttribute("aria-label", "Box height");
      HEIGHTS.forEach(function (s) {
        var o = document.createElement("option");
        o.value = String(s[0]);
        o.textContent = s[1];
        if (s[0] === f.h) o.selected = true;
        hsel.appendChild(o);
      });
      var spacer = document.createElement("span");
      spacer.className = "tlb-fieldrow__spacer";
      rest.appendChild(sel);
      rest.appendChild(hsel);
      rest.appendChild(spacer);
      [
        ["up", "\u2191", "Move up", i === 0],
        ["down", "\u2193", "Move down", i === S.fields.length - 1],
        ["remove", "\u00d7", "Remove this box", false],
      ].forEach(function (b) {
        var btn = document.createElement("button");
        btn.type = "button";
        btn.className = "tlb-iconbtn";
        btn.textContent = b[1];
        btn.setAttribute("data-act", b[0]);
        btn.setAttribute("aria-label", b[2]);
        btn.disabled = b[3];
        rest.appendChild(btn);
      });

      li.appendChild(on);
      li.appendChild(cap);
      li.appendChild(rest);
      fieldList.appendChild(li);
    });
    var add = q("[data-tlb-add-field]");
    add.disabled = S.fields.length >= MAX_FIELDS;
    qa("[data-chip]").forEach(function (c) {
      c.disabled = S.fields.length >= MAX_FIELDS;
    });
    if (focus) {
      var target = fieldList.querySelector('[data-uid="' + focus.uid + '"] [data-act="' + focus.act + '"]');
      if (target && !target.disabled) target.focus();
      else {
        var fallback = fieldList.querySelector('[data-uid="' + focus.uid + '"] [data-act="caption"]');
        if (fallback) fallback.focus();
      }
    }
  }

  function fieldFor(el) {
    var li = el.closest("[data-uid]");
    if (!li) return null;
    var id = li.getAttribute("data-uid");
    for (var i = 0; i < S.fields.length; i += 1) {
      if (S.fields[i].uid === id) return { f: S.fields[i], i: i, li: li };
    }
    return null;
  }

  function buildFieldEvents() {
    fieldList.addEventListener("focusin", function (e) {
      var hit = fieldFor(e.target);
      if (hit && ui.sel !== hit.f.uid) {
        ui.sel = hit.f.uid;
        schedule();
      }
    });
    fieldList.addEventListener("focusout", function (e) {
      if (e.relatedTarget && fieldList.contains(e.relatedTarget)) return;
      if (ui.sel) {
        ui.sel = null;
        schedule();
      }
    });
    fieldList.addEventListener("input", function (e) {
      var hit = fieldFor(e.target);
      if (!hit) return;
      if (e.target.getAttribute("data-act") === "caption") {
        hit.f.caption = e.target.value;
        schedule();
      }
    });
    fieldList.addEventListener("change", function (e) {
      var hit = fieldFor(e.target);
      if (!hit) return;
      var act = e.target.getAttribute("data-act");
      if (act === "on") {
        hit.f.on = e.target.checked;
        hit.li.classList.toggle("is-off", !hit.f.on);
      } else if (act === "span") {
        hit.f.span = Number(e.target.value);
      } else if (act === "height") {
        hit.f.h = Number(e.target.value);
      }
      schedule();
    });
    fieldList.addEventListener("click", function (e) {
      var btn = e.target.closest("button[data-act]");
      if (!btn) return;
      var hit = fieldFor(btn);
      if (!hit) return;
      var act = btn.getAttribute("data-act");
      if (act === "up" && hit.i > 0) {
        S.fields.splice(hit.i - 1, 0, S.fields.splice(hit.i, 1)[0]);
        renderFields({ uid: hit.f.uid, act: "up" });
      } else if (act === "down" && hit.i < S.fields.length - 1) {
        S.fields.splice(hit.i + 1, 0, S.fields.splice(hit.i, 1)[0]);
        renderFields({ uid: hit.f.uid, act: "down" });
      } else if (act === "remove") {
        var next = S.fields[hit.i + 1] || S.fields[hit.i - 1];
        S.fields.splice(hit.i, 1);
        renderFields(next ? { uid: next.uid, act: "caption" } : null);
        if (!next) q("[data-tlb-add-field]").focus();
      }
      schedule();
    });
    q("[data-tlb-add-field]").addEventListener("click", function () {
      addField("");
    });
    q("[data-tlb-reset-fields]").addEventListener("click", function () {
      S.fields = defaultFields();
      renderFields();
      schedule();
    });
    var chips = q("[data-tlb-chips]");
    CHIPS.forEach(function (text) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "tlb-chip";
      b.setAttribute("data-chip", text);
      b.textContent = "+ " + text;
      b.addEventListener("click", function () {
        addField(text);
      });
      chips.appendChild(b);
    });
  }

  /* A new box takes whatever is left of the last row, so it slots in. */
  function fillSpan() {
    var used = 12;
    S.fields.forEach(function (f) {
      if (!f.on) return;
      used = used + f.span > 12 ? f.span : used + f.span;
    });
    var left = 12 - used;
    var choices = [9, 8, 6, 4, 3, 2];
    for (var i = 0; i < choices.length; i += 1) {
      if (choices[i] <= left) return choices[i];
    }
    return 4;
  }

  function addField(caption) {
    if (S.fields.length >= MAX_FIELDS) return;
    var f = { uid: uid(), caption: caption, span: fillSpan(), h: 0, on: true };
    S.fields.push(f);
    renderFields({ uid: f.uid, act: "caption" });
    schedule();
  }

  /* Logo */

  var logoWarn = q("[data-tlb-logo-warn]");

  function textShown() {
    return !!S.heading.trim() || !!S.sub.trim();
  }

  function syncLogoUi() {
    var has = !!S.logo;
    var free = has && !!S.logoFree;
    q("[data-tlb-logo-opts]").hidden = !has;
    q("[data-tlb-logo-remove]").hidden = !has;
    q("[data-tlb-logo-name]").textContent = has ? S.logo.name : "PNG, JPG, SVG or WebP, up to 25MB";
    qa("[data-tlb-lay-opts]").forEach(function (el) { el.hidden = free; });
    qa("[data-tlb-free-opts]").forEach(function (el) { el.hidden = !free; });
    q("[data-tlb-logo-side-wrap]").hidden = free || S.layout === "centred";
    q("[data-tlb-panel-wrap]").hidden = !free && S.layout !== "banner";
    q("[data-tlb-sub-help]").hidden = S.layout !== "sidebar";
    var logoOnly = q("[data-tlb-logo-only]");
    logoOnly.checked = has && !textShown();
    q("[data-tlb-logo-only-wrap]").hidden = free;
    qa('input[name="tlb-logo-mode"]').forEach(function (r) {
      r.checked = r.value === (free ? "free" : "layout");
    });
    qa('input[name="tlb-logo-side"]').forEach(function (r) {
      r.checked = r.value === S.logoSide;
    });
    var sizeInput = q("[data-tlb-free-size]");
    if (free && sizeInput && document.activeElement !== sizeInput) sizeInput.value = String(Math.round(S.logoFree.w));
    var warn = "";
    if (has && !S.logo.vector) {
      var place = free ? freePlacement() : logoPlacement(geometry(S.layout, true), S.layout);
      if (place) {
        var needed = Math.round(place.w * LOGO_PPM);
        if (S.logo.w < needed * 0.6) {
          warn =
            "Your logo is " + S.logo.w + "px wide and prints best at about " + needed +
            "px at this size, so it may look soft. A larger PNG or an SVG will print sharper.";
        }
      }
    }
    logoWarn.hidden = !warn;
    logoWarn.textContent = warn;
  }

  function setLogoOnly(on) {
    var heading = q("#tlb-heading");
    var sub = q("#tlb-sub");
    if (on) {
      ui.stash = { heading: S.heading, sub: S.sub };
      S.heading = "";
      S.sub = "";
    } else if (ui.stash) {
      S.heading = ui.stash.heading;
      S.sub = ui.stash.sub;
      ui.stash = null;
    } else {
      heading.focus();
    }
    heading.value = S.heading;
    sub.value = S.sub;
    schedule();
  }

  /* Sizes and spacing */

  function headValue() {
    var r = inspect().range;
    return clamp(S.headH == null ? r.def : S.headH, r.min, r.max);
  }

  function sideValue() {
    return clamp(S.sideW == null ? 22 : S.sideW, 12, 44);
  }

  function syncSizes() {
    var insp = inspect();
    var head = q("#tlb-head-h");
    if (head) {
      head.min = String(insp.range.min);
      head.max = String(insp.range.max);
      if (document.activeElement !== head) head.value = String(Math.round(headValue()));
      q("[data-tlb-head-label]").textContent = S.layout === "banner" ? "Banner height" : "Header height";
      q('[data-tlb-val="headH"]').textContent = Math.round(headValue()) + " mm";
    }
    var side = q("#tlb-side-w");
    q("[data-tlb-side-wrap]").hidden = S.layout !== "sidebar";
    if (side) {
      if (document.activeElement !== side) side.value = String(Math.round(sideValue()));
      q('[data-tlb-val="sideW"]').textContent = Math.round(sideValue()) + " mm";
    }
    var title = q("#tlb-title-size");
    var scale = S.titleScale == null ? 100 : S.titleScale;
    if (title) {
      if (document.activeElement !== title) title.value = String(scale);
      q('[data-tlb-val="titleScale"]').textContent = Math.round(scale) + "%";
    }
    q("[data-tlb-title-wrap]").hidden = !insp.hasText;
    q("[data-tlb-align-wrap]").hidden = !insp.hasText;
    qa('input[name="tlb-align"]').forEach(function (r) {
      r.checked = r.value === insp.g.align;
    });
    qa('input[name="tlb-lw"]').forEach(function (r) {
      r.checked = Number(r.value) === S.lineW;
    });
  }

  function syncFit() {
    var el = q("[data-tlb-fit]");
    if (!el) return;
    var boxes = inspect().boxes;
    var text = "";
    var flag = "";
    if (!boxes.length) {
      text = "There are no boxes on the label. Add some under Boxes to write in.";
    } else {
      var min = Infinity;
      boxes.forEach(function (b) { min = Math.min(min, b.h); });
      text =
        boxes.length + (boxes.length === 1 ? " box" : " boxes") + " in " + boxes.rows +
        (boxes.rows === 1 ? " row" : " rows") + ", at least " + Math.round(min) + "mm tall to write in.";
      if (boxes.cap > 12) text += " They have been rearranged to fit more in each row.";
      if (boxes.unit < 12) {
        text += " That is tight for handwriting. Make the header smaller or remove a box.";
        flag = "warn";
      }
    }
    el.textContent = text;
    if (flag) el.setAttribute("data-flag", flag);
    else el.removeAttribute("data-flag");
  }

  function syncHistoryUi() {
    var u = q("[data-tlb-undo]");
    var r = q("[data-tlb-redo]");
    if (u) u.disabled = !hist.undo.length;
    if (r) r.disabled = !hist.redo.length;
  }

  var WIDE = /contents|notes|venue|description|address|comments/i;

  /* Evens the boxes out into rows of one to four, with long ones full width. */
  function autoArrange() {
    var runs = [];
    var cur = [];
    S.fields.forEach(function (f) {
      if (!f.on) return;
      if (WIDE.test(f.caption) || f.caption.length > 16) {
        if (cur.length) runs.push(cur);
        cur = [];
        runs.push([f]);
      } else cur.push(f);
    });
    if (cur.length) runs.push(cur);
    runs.forEach(function (run) {
      if (run.length === 1 && (WIDE.test(run[0].caption) || run[0].caption.length > 16)) {
        run[0].span = 12;
        if (/contents|notes|comments|description/i.test(run[0].caption) && run[0].h === 0) run[0].h = 1;
        return;
      }
      var k = run.length;
      var per = k <= 4 ? k : k % 4 === 0 ? 4 : k % 3 === 0 ? 3 : 4;
      var rows = Math.ceil(k / per);
      var base = Math.floor(k / rows);
      var extra = k % rows;
      var at = 0;
      for (var r = 0; r < rows; r += 1) {
        var m = base + (r < extra ? 1 : 0);
        for (var i = 0; i < m; i += 1) run[at + i].span = 12 / m;
        at += m;
      }
    });
    renderFields();
    schedule();
  }

  function resetSizes() {
    S.headH = null;
    S.sideW = null;
    S.titleScale = null;
    S.align = null;
    S.lineW = 1;
    schedule();
  }

  function startFree() {
    if (!S.logo || S.logoFree) return;
    var p = logoPlacement(geometry(S.layout, true), S.layout);
    S.logoFree = p
      ? { x: p.x + p.w / 2, y: p.y + p.h / 2, w: clamp(p.w, 10, 180) }
      : { x: W / 2, y: H / 2, w: 60 };
    schedule();
  }

  function nudgeLogo(dx, dy) {
    if (!S.logoFree) return;
    S.logoFree.x = clamp(S.logoFree.x + dx, 0, W);
    S.logoFree.y = clamp(S.logoFree.y + dy, 0, H);
    schedule();
  }

  /* The preview: grips resize the header, a box or the title is click to edit,
     boxes drag onto each other to reorder, a placed logo drags anywhere. */

  var act = null;
  var suppressClick = false;

  function mmPoint(clientX, clientY) {
    var r = canvas.getBoundingClientRect();
    return { x: ((clientX - r.left) / r.width) * W, y: ((clientY - r.top) / r.height) * H };
  }

  function inside(m, r, slack) {
    return m.x >= r.x - slack && m.x <= r.x + r.w + slack && m.y >= r.y - slack && m.y <= r.y + r.h + slack;
  }

  function hitTest(m, slack) {
    if (S.logoFree) {
      var p = freePlacement();
      if (p) {
        var corner = { x: p.x + p.w + 1, y: p.y + p.h + 1 };
        if (Math.abs(m.x - corner.x) <= slack + 1 && Math.abs(m.y - corner.y) <= slack + 1) {
          return { kind: "logosize", p: p };
        }
        if (inside(m, p, slack)) return { kind: "logo" };
      }
    }
    var insp = inspect();
    for (var i = 0; i < insp.handles.length; i += 1) {
      var h = insp.handles[i];
      var along = h.axis === "y" ? m.x : m.y;
      var across = h.axis === "y" ? m.y : m.x;
      if (Math.abs(across - h.pos) <= slack && along >= h.from - slack && along <= h.to + slack) {
        return { kind: "handle", h: h };
      }
    }
    for (var j = 0; j < insp.boxes.length; j += 1) {
      if (inside(m, insp.boxes[j], 0)) return { kind: "box", b: insp.boxes[j] };
    }
    var tb = insp.g.textBox;
    if (tb && !S.logoFree && inside(m, { x: tb.x, y: tb.y, w: Math.max(tb.w, 30), h: tb.h }, 0)) return { kind: "title" };
    return null;
  }

  function setCursor(hit) {
    var c = "";
    if (hit) {
      if (hit.kind === "logo") c = "grab";
      else if (hit.kind === "logosize") c = "nwse";
      else if (hit.kind === "handle") c = hit.h.axis === "y" ? "ns" : "ew";
      else c = "pick";
    }
    if (c) canvas.setAttribute("data-cursor", c);
    else canvas.removeAttribute("data-cursor");
  }

  function beginAct(m, touch) {
    var hit = hitTest(m, touch ? 5 : 2.5);
    if (!hit) return false;
    if (hit.kind === "logo") {
      act = { type: "logo", dx: S.logoFree.x - m.x, dy: S.logoFree.y - m.y };
    } else if (hit.kind === "logosize") {
      act = { type: "logosize", ox: hit.p.x, oy: hit.p.y, ar: hit.p.h / hit.p.w };
    } else if (hit.kind === "handle") {
      act = { type: "handle", h: hit.h };
    } else if (hit.kind === "box" && !touch) {
      act = { type: "box", b: hit.b, start: m, moved: false };
    } else return false;
    canvas.classList.add("is-dragging");
    return true;
  }

  function moveAct(m) {
    if (!act) return;
    if (act.type === "logo") {
      var x = m.x + act.dx;
      if (Math.abs(x - W / 2) < 2) x = W / 2;
      S.logoFree.x = clamp(x, 0, W);
      S.logoFree.y = clamp(m.y + act.dy, 0, H);
    } else if (act.type === "logosize") {
      var nw = clamp(m.x - act.ox - 1, 10, Math.min(180, (H - 2) / act.ar));
      S.logoFree.w = nw;
      S.logoFree.x = act.ox + nw / 2;
      S.logoFree.y = act.oy + (nw * act.ar) / 2;
    } else if (act.type === "handle") {
      var range = inspect().range;
      if (act.h.id === "head") {
        var layoutShift = S.layout === "centred" ? 3 : 0;
        S.headH = Math.round(clamp(m.y + layoutShift, range.min, range.max));
      } else {
        S.sideW = Math.round(clamp(m.x, 12, 44));
      }
    } else if (act.type === "box") {
      var dist = Math.abs(m.x - act.start.x) + Math.abs(m.y - act.start.y);
      if (!act.moved && dist > 3) {
        act.moved = true;
        ui.sel = act.b.f.uid;
      }
      if (act.moved) {
        var over = null;
        inspect().boxes.forEach(function (b) {
          if (b.f !== act.b.f && inside(m, b, 0)) over = b.f.uid;
        });
        ui.drop = over;
      }
    }
    schedule();
  }

  function endAct() {
    if (!act) return;
    var done = act;
    act = null;
    canvas.classList.remove("is-dragging");
    suppressClick = done.type !== "box" || done.moved;
    if (done.type === "box" && done.moved && ui.drop) {
      var from = S.fields.indexOf(done.b.f);
      var to = -1;
      S.fields.forEach(function (f, i) {
        if (f.uid === ui.drop) to = i;
      });
      if (from >= 0 && to >= 0) {
        S.fields.splice(to, 0, S.fields.splice(from, 1)[0]);
        renderFields();
      }
    }
    if (done.type === "box" && done.moved) ui.sel = null;
    ui.drop = null;
    schedule();
  }

  function focusBox(uid) {
    var details = q("[data-tlb-boxes]");
    if (details) details.open = true;
    ui.sel = uid;
    var input = fieldList.querySelector('[data-uid="' + uid + '"] [data-act="caption"]');
    if (input) input.focus();
    schedule();
  }

  function adjustByKey(key, big) {
    var step = big ? 5 : 1;
    if (S.logoFree) {
      var d = { ArrowUp: [0, -step], ArrowDown: [0, step], ArrowLeft: [-step, 0], ArrowRight: [step, 0] }[key];
      if (!d) return false;
      nudgeLogo(d[0], d[1]);
      return true;
    }
    var range = inspect().range;
    if (key === "ArrowUp" || key === "ArrowDown") {
      S.headH = clamp(Math.round(headValue()) + (key === "ArrowDown" ? step : -step), range.min, range.max);
      schedule();
      return true;
    }
    if (S.layout === "sidebar" && (key === "ArrowLeft" || key === "ArrowRight")) {
      S.sideW = clamp(Math.round(sideValue()) + (key === "ArrowRight" ? step : -step), 12, 44);
      schedule();
      return true;
    }
    return false;
  }

  function bindDrag() {
    if (!canvas) return;
    canvas.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "touch" || e.button > 0) return;
      suppressClick = false;
      if (beginAct(mmPoint(e.clientX, e.clientY), false)) {
        e.preventDefault();
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch (err) {
          /* synthetic pointer: nothing to capture */
        }
        canvas.focus({ preventScroll: true });
      }
    });
    canvas.addEventListener("pointermove", function (e) {
      if (e.pointerType === "touch") return;
      var m = mmPoint(e.clientX, e.clientY);
      if (act) moveAct(m);
      else setCursor(hitTest(m, 2.5));
    });
    ["pointerup", "pointercancel"].forEach(function (n) {
      canvas.addEventListener(n, endAct);
    });
    canvas.addEventListener("pointerleave", function () {
      if (!act) setCursor(null);
    });
    canvas.addEventListener("click", function (e) {
      if (suppressClick) {
        suppressClick = false;
        return;
      }
      var hit = hitTest(mmPoint(e.clientX, e.clientY), 0);
      if (!hit) return;
      if (hit.kind === "box") focusBox(hit.b.f.uid);
      else if (hit.kind === "title") q("#tlb-heading").focus();
    });
    canvas.addEventListener(
      "touchstart",
      function (e) {
        var t = e.touches[0];
        suppressClick = false;
        if (t && beginAct(mmPoint(t.clientX, t.clientY), true)) e.preventDefault();
      },
      { passive: false },
    );
    canvas.addEventListener(
      "touchmove",
      function (e) {
        if (!act) return;
        e.preventDefault();
        var t = e.touches[0];
        if (t) moveAct(mmPoint(t.clientX, t.clientY));
      },
      { passive: false },
    );
    canvas.addEventListener("touchend", endAct);
    canvas.addEventListener("touchcancel", endAct);
    canvas.addEventListener("keydown", function (e) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (adjustByKey(e.key, e.shiftKey)) e.preventDefault();
    });

    document.addEventListener("keydown", function (e) {
      if (S.mode !== "design" || S.reorder) return;
      var mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      var t = e.target;
      var tag = t && t.tagName;
      if (tag === "TEXTAREA" || tag === "SELECT" || (tag === "INPUT" && t.type !== "range" && t.type !== "checkbox" && t.type !== "radio")) return;
      var key = (e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        redo();
      }
    });

    qa("[data-tlb-nudge]").forEach(function (b) {
      b.addEventListener("click", function () {
        var d = b.getAttribute("data-tlb-nudge").split(",");
        nudgeLogo(Number(d[0]), Number(d[1]));
      });
    });
    q("[data-tlb-logo-centre]").addEventListener("click", function () {
      if (S.logoFree) {
        S.logoFree.x = W / 2;
        schedule();
      }
    });
    q("[data-tlb-free-size]").addEventListener("input", function (e) {
      if (S.logoFree) {
        S.logoFree.w = Number(e.target.value);
        schedule();
      }
    });
    qa('input[name="tlb-logo-mode"]').forEach(function (r) {
      r.addEventListener("change", function () {
        if (!r.checked) return;
        if (r.value === "free") startFree();
        else {
          S.logoFree = null;
          schedule();
        }
      });
    });
    q("[data-tlb-logo-only]").addEventListener("change", function (e) {
      setLogoOnly(e.target.checked);
    });

    qa("[data-tlb-style]").forEach(function (el) {
      el.addEventListener("input", function () {
        S[el.getAttribute("data-tlb-style")] = Number(el.value);
        schedule();
      });
    });
    qa('input[name="tlb-align"]').forEach(function (r) {
      r.addEventListener("change", function () {
        if (r.checked) {
          S.align = r.value;
          schedule();
        }
      });
    });
    qa('input[name="tlb-lw"]').forEach(function (r) {
      r.addEventListener("change", function () {
        if (r.checked) {
          S.lineW = Number(r.value);
          schedule();
        }
      });
    });
    q("[data-tlb-autoarrange]").addEventListener("click", autoArrange);
    q("[data-tlb-reset-sizes]").addEventListener("click", resetSizes);
    q("[data-tlb-undo]").addEventListener("click", undo);
    q("[data-tlb-redo]").addEventListener("click", redo);
    q("[data-tlb-start-again]").addEventListener("click", startAgain);
  }

  function hsv(r, g, b) {
    var mx = Math.max(r, g, b);
    var mn = Math.min(r, g, b);
    var d = mx - mn;
    var h = 0;
    if (d) {
      if (mx === r) h = ((g - b) / d) % 6;
      else if (mx === g) h = (b - r) / d + 2;
      else h = (r - g) / d + 4;
      h *= 60;
      if (h < 0) h += 360;
    }
    return { h: h, s: mx ? d / mx : 0, v: mx / 255 };
  }

  /* One key per print colour: black, a grey, or a 30 degree slice of the hue wheel. */
  function inkKey(hex) {
    var c = rgb(hex);
    var k = hsv(c[0], c[1], c[2]);
    if (k.v < 0.22) return "black";
    if (k.s < 0.3) return "grey";
    return "h" + Math.floor(k.h / 30);
  }

  function logoInkKeys(img) {
    try {
      var nw = img.naturalWidth || 96;
      var nh = img.naturalHeight || 96;
      var k = Math.min(1, 96 / Math.max(nw, nh));
      var c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(nw * k));
      c.height = Math.max(1, Math.round(nh * k));
      var x = c.getContext("2d");
      x.drawImage(img, 0, 0, c.width, c.height);
      var d = x.getImageData(0, 0, c.width, c.height).data;
      var counts = {};
      var total = 0;
      for (var i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 128) continue;
        if (Math.min(d[i], d[i + 1], d[i + 2]) > 225) continue;
        var key = inkKey("#" + [d[i], d[i + 1], d[i + 2]].map(function (n) { return (n < 16 ? "0" : "") + n.toString(16); }).join(""));
        if (key === "grey") continue;
        counts[key] = (counts[key] || 0) + 1;
        total += 1;
      }
      return Object.keys(counts).filter(function (key) {
        return counts[key] >= Math.max(6, total * 0.04);
      });
    } catch (e) {
      return ["logo-unknown"];
    }
  }

  function loadLogo(file, silent) {
    var ext = (file.name.split(".").pop() || "").toLowerCase();
    if (["png", "jpg", "jpeg", "svg", "webp"].indexOf(ext) < 0) {
      showError("Logos must be PNG, JPG, SVG or WebP. If you have an AI, EPS or PDF logo, choose \u201cI have my own artwork\u201d.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showError("That logo is larger than 25MB.");
      return;
    }
    clearError();
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      if (S.logo) URL.revokeObjectURL(S.logo.url);
      S.logo = {
        file: file,
        url: url,
        img: img,
        name: file.name,
        w: img.naturalWidth || 400,
        h: img.naturalHeight || 400,
        vector: ext === "svg",
        inks: logoInkKeys(img),
      };
      if (silent) hist.cur = null;
      schedule();
    };
    img.onerror = function () {
      URL.revokeObjectURL(url);
      showError("We could not read that logo. Try a PNG or JPG export of it.");
    };
    img.src = url;
  }

  /* Print colours: black is the quoted price; anything else is checked with the mock-up. */

  function stockName() {
    var st = STOCK.filter(function (x) { return x.hex === S.bg.toLowerCase(); })[0];
    return st ? st.name : "White";
  }

  var MAX_EXTRA = 3;

  function printInks() {
    var seen = {};
    function add(hex) {
      if (!hex || hex.toLowerCase() === S.bg.toLowerCase()) return;
      seen[inkKey(hex)] = true;
    }
    var g = geometry(S.layout, false);
    add(S.text);
    add(S.accent);
    add(g.headHex);
    if (g.vertical) add(g.vertical.colour);
    if (S.logo && S.logo.inks) {
      S.logo.inks.forEach(function (key) {
        seen[key] = true;
      });
    }
    var count = Object.keys(seen).length || 1;
    return { count: count, blackOnly: count === 1 && !!seen.black };
  }

  function inksLabel(inks) {
    if (inks.blackOnly) return "Black only";
    return inks.count + (inks.count === 1 ? " colour" : " colours");
  }

  function syncInks() {
    var el = q("[data-tlb-inks]");
    if (!el) return;
    var inks = printInks();
    var stock = stockName();
    var v = selectedVariant();
    var cc = colourCharge(v);
    var extra = Math.max(0, inks.count - 1);
    var parts = [];
    var flag = "ok";
    if (inks.blackOnly && stock === "White") {
      parts.push("Prints in black on a white label. No colour charge.");
    } else {
      if (!inks.blackOnly || extra) {
        flag = "check";
        var line = "Prints in " + inksLabel(inks).toLowerCase() + ".";
        if (cc.variant && cc.extra) {
          line +=
            " Each colour after the first adds " + money(cc.each) + " ex VAT on a pack of " + v.count +
            ", so " + money(cc.total) + " for this design, already in your total.";
        }
        if (extra > MAX_EXTRA) {
          line += " That is more than four colours, so we confirm the final price with your mock-up before anything is printed.";
        }
        parts.push(line);
      }
      if (stock !== "White") {
        flag = "check";
        parts.push("Printed on " + stock.toLowerCase() + " stock, which can cost more. We confirm that with your mock-up before anything is printed.");
      }
    }
    el.textContent = parts.join(" ");
    el.setAttribute("data-flag", flag);
  }

  /* Order panel */

  function money(pence) {
    return "\u00a3" + (pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function selectedVariant() {
    var r = q('input[name="tlb-pack"]:checked');
    if (!r) return null;
    for (var i = 0; i < cfg.variants.length; i += 1) {
      if (String(cfg.variants[i].id) === r.value) return cfg.variants[i];
    }
    return null;
  }

  function parseColours(text) {
    var m = /^(\d+) colours?$/.exec(String(text || ""));
    return m ? parseInt(m[1], 10) : 1;
  }

  function extraColours() {
    var n = 1;
    if (S.reorder) n = parseColours((S.reorder.properties || {})["Print colours"]);
    else if (S.mode === "design") n = printInks().count;
    return Math.max(0, n - 1);
  }

  function colourVariantFor(count) {
    if (!cfg.colour) return null;
    for (var i = 0; i < cfg.colour.length; i += 1) {
      if (cfg.colour[i].count === count && cfg.colour[i].available !== false) return cfg.colour[i];
    }
    return null;
  }

  function colourCharge(v) {
    var extra = Math.min(MAX_EXTRA, extraColours());
    var variant = v && extra ? colourVariantFor(v.count) : null;
    return { extra: extra, variant: variant, each: variant ? variant.price : 0, total: variant ? variant.price * extra : 0 };
  }

  function feeApplies() {
    return !!cfg.fee && !S.reorder;
  }

  function updateSummary() {
    var v = selectedVariant();
    var fee = feeApplies();
    var feeRow = q("[data-tlb-sum-fee-row]");
    feeRow.hidden = !cfg.fee;
    if (!v) return;
    var cc = colourCharge(v);
    var total = v.price + (fee ? cfg.fee.price : 0) + cc.total;
    var colourRow = q("[data-tlb-sum-colour-row]");
    if (colourRow) {
      colourRow.hidden = !cc.variant;
      if (cc.variant) {
        q("[data-tlb-sum-colour-label]").textContent =
          cc.extra + (cc.extra === 1 ? " extra print colour" : " extra print colours");
        q("[data-tlb-sum-colour]").textContent = money(cc.total);
      }
    }
    q("[data-tlb-sum-pack]").textContent = v.count + " labels, " + money(v.price);
    if (cfg.fee) q("[data-tlb-sum-fee]").textContent = fee ? money(cfg.fee.price) : "Waived (reorder)";
    q("[data-tlb-sum-total]").textContent = money(total);
    var qbTotal = q("[data-tlb-qb-total]");
    if (qbTotal) {
      qbTotal.textContent = money(total) + " ex VAT";
      q("[data-tlb-qb-detail]").textContent =
        v.count + " labels" + (fee ? " + artwork setup" : S.reorder ? ", reorder" : "") + (cc.variant ? " + colours" : "") + " | " + money(Math.round(total * 1.2)) + " inc VAT";
    }
    q("[data-tlb-sum-inc]").textContent = money(Math.round(total * 1.2));
  }

  /* Own artwork */

  var ART_EXT = ["png", "jpg", "jpeg", "svg", "webp", "pdf", "ai", "eps"];

  function renderArtList() {
    var list = q("[data-tlb-art-list]");
    list.innerHTML = "";
    var preview = q("[data-tlb-upload-preview]");
    preview.innerHTML = "";
    S.ownFiles.forEach(function (f, i) {
      var li = document.createElement("li");
      var span = document.createElement("span");
      span.textContent = f.name + " (" + (f.size / 1048576).toFixed(1) + "MB)";
      var rm = document.createElement("button");
      rm.type = "button";
      rm.className = "tlb-link";
      rm.textContent = "Remove";
      rm.setAttribute("aria-label", "Remove " + f.name);
      rm.addEventListener("click", function () {
        S.ownFiles.splice(i, 1);
        renderArtList();
      });
      li.appendChild(span);
      li.appendChild(rm);
      list.appendChild(li);
      if (/\.(png|jpe?g|webp|svg)$/i.test(f.name)) {
        var img = document.createElement("img");
        img.alt = "Preview of " + f.name;
        img.src = URL.createObjectURL(f);
        preview.appendChild(img);
      }
    });
    if (!preview.children.length) {
      var p = document.createElement("p");
      p.className = "tlb-upload-preview__empty";
      p.textContent = S.ownFiles.length
        ? "Your artwork is attached to your order. We prepare your mock-up from it."
        : "Add your finished artwork on the right and it is attached to your order. We prepare your mock-up from it.";
      preview.appendChild(p);
    }
  }

  function addOwnFiles(files) {
    clearError();
    var problems = [];
    Array.prototype.slice.call(files).forEach(function (f) {
      var ext = (f.name.split(".").pop() || "").toLowerCase();
      if (ART_EXT.indexOf(ext) < 0) problems.push(f.name + " is not a supported file type.");
      else if (f.size > 25 * 1024 * 1024) problems.push(f.name + " is larger than 25MB.");
      else if (S.ownFiles.length >= 3) problems.push("You can attach up to three files.");
      else S.ownFiles.push(f);
    });
    if (problems.length) showError(problems[0]);
    renderArtList();
  }

  /* Mode, errors, status */

  function applyVisibility() {
    var reorder = !!S.reorder;
    qa("[data-tlb-design-only]").forEach(function (n) {
      n.hidden = reorder || S.mode !== "design";
    });
    qa("[data-tlb-upload-only]").forEach(function (n) {
      n.hidden = reorder || S.mode !== "upload";
    });
    qa("[data-tlb-hide-reorder]").forEach(function (n) {
      n.hidden = reorder;
    });
    qa("[data-tlb-reorder-only]").forEach(function (n) {
      n.hidden = !reorder;
    });
    root.setAttribute("data-reorder", reorder ? "1" : "0");
  }

  function setMode(mode) {
    S.mode = mode;
    root.setAttribute("data-mode", mode);
    applyVisibility();
    clearError();
    if (mode === "upload") renderArtList();
    schedule();
  }

  var errorBox = q("[data-tlb-error]");
  var statusBox = q("[data-tlb-status]");
  function showError(msg) {
    errorBox.textContent = msg;
    errorBox.hidden = false;
  }
  function clearError() {
    errorBox.hidden = true;
    errorBox.textContent = "";
  }
  function setStatus(msg) {
    statusBox.textContent = msg || "";
    statusBox.hidden = !msg;
  }

  /* ------------------------------------------------------------- fonts */

  var fontPromise = null;
  function loadFonts() {
    if (!document.fonts || !document.fonts.load) return Promise.resolve();
    var jobs = Object.keys(FONTS).map(function (key) {
      var f = FONTS[key];
      var first = f.family.split(",")[0];
      return document.fonts.load(f.weight + " 24px " + first, "Aa 123 ABC").catch(function () {});
    });
    jobs.push(document.fonts.load("600 24px " + FONTS.condensed.family.split(",")[0], "Aa").catch(function () {}));
    fontPromise = Promise.all(jobs);
    return fontPromise;
  }

  /* ----------------------------------------------------------- ordering */

  var busy = false;

  function toBlob(c) {
    return new Promise(function (resolve, reject) {
      c.toBlob(function (b) {
        if (b) resolve(b);
        else reject(new Error("Could not create the design image."));
      }, "image/png");
    });
  }

  function renderBlob(ppm) {
    var c = document.createElement("canvas");
    c.width = Math.round(W * ppm);
    c.height = Math.round(H * ppm);
    drawLabel(c.getContext("2d"), c.width / W, { ghost: false, watermark: true });
    return toBlob(c);
  }

  function validate() {
    if (!S.reorder && S.mode === "design" && !S.heading.trim() && !S.logo) {
      q("#tlb-heading").focus();
      return "Add your tour or production name, or a logo, so we know what to print.";
    }
    if (!S.reorder && S.mode === "upload" && !S.ownFiles.length) {
      return "Add your artwork file first.";
    }
    var v = selectedVariant();
    if (!v || !v.available) return "Choose a pack size.";
    return "";
  }

  /* An owner's preview link (Growth OS → Builders → Preview) carries a signed
     token; the endpoints accept it while the builder is switched off. */
  function builderPreviewToken(key) {
    var stored = "ts-builder-preview:" + key;
    try {
      var given = new URLSearchParams(window.location.search).get("ts_builder_preview");
      if (given) window.sessionStorage.setItem(stored, given);
      return given || window.sessionStorage.getItem(stored) || "";
    } catch (e) {
      return "";
    }
  }

  var PREVIEW = builderPreviewToken("labels");

  function postJson(url, body) {
    var headers = { "content-type": "application/json" };
    if (PREVIEW) headers["x-builder-preview"] = PREVIEW;
    return fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(
        function (j) {
          if (!r.ok || !j.ok) throw new Error(j.error || "We could not save your design.");
          return j;
        },
        function () {
          throw new Error("We could not save your design.");
        },
      );
    });
  }

  function xhrUpload(target, blob, name) {
    return new Promise(function (resolve, reject) {
      var form = new FormData();
      target.parameters.forEach(function (p) {
        form.append(p.name, p.value);
      });
      form.append("file", blob, name);
      var xhr = new XMLHttpRequest();
      xhr.open("POST", target.url);
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error("Upload failed (" + xhr.status + ")."));
      };
      xhr.onerror = function () {
        reject(new Error("Upload failed. Check your connection and try again."));
      };
      xhr.send(form);
    });
  }

  function prepareFiles() {
    var ready = fontPromise || loadFonts();
    return ready.then(function () {
      if (S.mode === "upload") {
        return S.ownFiles.map(function (f) {
          return { kind: "artwork", name: f.name, type: f.type || "application/octet-stream", blob: f };
        });
      }
      return renderBlob(PREVIEW_PPM).then(function (proof) {
        var files = [{ kind: "proof", name: "proof.png", type: "image/png", blob: proof }];
        if (S.logo && S.logo.file) {
          files.push({ kind: "logo", name: S.logo.file.name, type: S.logo.file.type || "application/octet-stream", blob: S.logo.file });
        }
        return files;
      });
    });
  }

  function round1(n) {
    return Math.round(n * 10) / 10;
  }

  function styleSpec() {
    var style = {};
    if (S.headH != null) style.hh = round1(S.headH);
    if (S.sideW != null) style.sw = round1(S.sideW);
    if (S.titleScale != null) style.ts = Math.round(S.titleScale);
    if (S.align) style.al = S.align;
    if (S.lineW !== 1) style.lw = S.lineW;
    return style;
  }

  /* The name shown on the basket line and used for file names. */
  function designName() {
    var heading = S.heading.trim();
    if (heading) return heading;
    if (!S.logo) return "";
    return ("Logo: " + S.logo.name.replace(/\.[a-z0-9]+$/i, "")).slice(0, 40);
  }

  function specJson(v) {
    return JSON.stringify({
      v: 1,
      mode: S.mode,
      layout: S.layout,
      heading: S.heading.trim(),
      sub: S.sub.trim(),
      caps: S.caps,
      font: S.font,
      colours: [S.accent, S.text, S.bg],
      logo: S.logo
        ? [S.logoSide, S.logoScale, S.logoPanel]
            .concat(S.logoFree ? [1, round1(S.logoFree.x), round1(S.logoFree.y), round1(S.logoFree.w)] : [])
        : null,
      fields: S.fields.map(function (f) {
        return [f.caption, f.span, f.h, f.on ? 1 : 0];
      }),
      style: styleSpec(),
      pack: v.count,
    });
  }

  function cartItems(v, registered, code) {
    var byKind = {};
    registered.forEach(function (f) {
      (byKind[f.kind] = byKind[f.kind] || []).push(f);
    });
    var fee = feeApplies();
    var props = { _design_id: S.designId };
    var feeLabel;
    if (S.mode === "design") {
      props.Heading = designName();
      if (S.sub.trim()) props["Second line"] = S.sub.trim();
      props.Layout = (LAYOUTS.filter(function (l) { return l.id === S.layout; })[0] || {}).name;
      props.Logo = S.logo ? S.logo.name : "None";
      props["Print colours"] = inksLabel(printInks());
      props["Label stock"] = stockName();
      props._proof = byKind.proof[0].url;
      if (byKind.logo) props._logo_file = byKind.logo[0].url;
      props._spec = specJson(v);
      feeLabel = designName();
    } else {
      props["Print colours"] = "Own artwork, to be checked";
      props.Artwork = "Own artwork uploaded (" + S.ownFiles.length + (S.ownFiles.length === 1 ? " file)" : " files)");
      (byKind.artwork || []).forEach(function (f, i) {
        props["_artwork_" + (i + 1)] = f.url;
      });
      feeLabel = "Own artwork";
    }
    if (code) props["Reorder code"] = code;
    if (cfg.fee) props.Setup = "New design, artwork setup charged";
    if (S.notes.trim()) props.Notes = S.notes.trim();
    var items = [{ id: v.id, quantity: 1, properties: props }];
    var cc = colourCharge(v);
    if (cc.variant && S.mode === "design") {
      items.push({
        id: cc.variant.id,
        quantity: cc.extra,
        properties: { "Extra colours for": feeLabel, _design_id: S.designId },
      });
    }
    if (fee) {
      items.push({
        id: cfg.fee.id,
        quantity: 1,
        properties: { "Setup for": feeLabel, _design_id: S.designId },
      });
    }
    return items;
  }

  function reorderItems(v) {
    var r = S.reorder;
    var props = {};
    Object.keys(r.properties).forEach(function (k) {
      props[k] = r.properties[k];
    });
    if (props._spec) {
      try {
        var spec = JSON.parse(props._spec);
        spec.pack = v.count;
        props._spec = JSON.stringify(spec);
      } catch (e) {
        /* keep the stored spec as it is */
      }
    }
    props["Reorder code"] = r.code;
    props.Setup = "Reorder, no setup charge";
    if (S.notes.trim()) props.Notes = S.notes.trim();
    var lines = [{ id: v.id, quantity: 1, properties: props }];
    var cc = colourCharge(v);
    if (cc.variant) {
      lines.push({
        id: cc.variant.id,
        quantity: cc.extra,
        properties: { "Extra colours for": props.Heading || "Reorder", _design_id: props._design_id || "" },
      });
    }
    return lines;
  }

  function postCart(items) {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json().then(
        function (j) {
          if (!r.ok) throw new Error(j.description || j.message || "We could not add that to your basket.");
          return j;
        },
        function () {
          throw new Error("We could not add that to your basket.");
        },
      );
    });
  }

  function showDone(code) {
    setStatus("");
    q("[data-tlb-add]").hidden = true;
    root.setAttribute("data-done", "1");
    var done = q("[data-tlb-done]");
    var codeLine = q("[data-tlb-done-code]");
    if (codeLine) {
      codeLine.hidden = !code;
      if (code) q("[data-tlb-done-code-value]").textContent = code;
    }
    done.hidden = false;
    q("[data-tlb-done] a").focus();
  }

  function addToBasket() {
    if (busy) return;
    clearError();
    var problem = validate();
    if (problem) {
      showError(problem);
      return;
    }
    var v = selectedVariant();
    var btn = q("[data-tlb-add]");
    busy = true;
    btn.disabled = true;
    if (S.reorder) {
      setStatus("Adding to your basket...");
      postCart(reorderItems(v))
        .then(function () {
          showDone(S.reorder.code);
        })
        .catch(function (e) {
          setStatus("");
          showError((e && e.message ? e.message : "Something went wrong.") + " You can try again, or email info@tourstock.co.uk.");
        })
        .then(function () {
          busy = false;
          btn.disabled = false;
        });
      return;
    }
    setStatus("Preparing your design...");
    var files;
    prepareFiles()
      .then(function (prepared) {
        files = prepared;
        setStatus("Saving your design...");
        return postJson(cfg.endpoint + "/uploads", {
          designId: S.designId,
          files: files.map(function (f) {
            return { filename: f.name, mimeType: f.type, size: f.blob.size, kind: f.kind };
          }),
        });
      })
      .then(function (res) {
        return Promise.all(
          files.map(function (f, i) {
            return xhrUpload(res.targets[i], f.blob, f.name);
          }),
        ).then(function () {
          return res.targets;
        });
      })
      .then(function (targets) {
        return postJson(cfg.endpoint + "/register", {
          designId: S.designId,
          files: files.map(function (f, i) {
            return { kind: f.kind, filename: f.name, resourceUrl: targets[i].resourceUrl };
          }),
        });
      })
      .then(function (res) {
        setStatus("Adding to your basket...");
        return postCart(cartItems(v, res.files, res.reorderCode)).then(function () {
          return res.reorderCode;
        });
      })
      .then(function (code) {
        showDone(code);
      })
      .catch(function (e) {
        setStatus("");
        showError(
          (e && e.message ? e.message : "Something went wrong.") +
            " You can try again, or email info@tourstock.co.uk with your artwork.",
        );
      })
      .then(function () {
        busy = false;
        btn.disabled = false;
      });
  }

  /* ------------------------------------------------------------- reorder */

  function syncControls() {
    qa("[data-tlb-input]").forEach(function (el) {
      var key = el.getAttribute("data-tlb-input");
      if (el.type === "checkbox") el.checked = !!S[key];
      else if (el.type === "radio") el.checked = el.value === S[key];
      else el.value = S[key] == null ? "" : String(S[key]);
    });
    qa('input[name="tlb-layout"]').forEach(function (r) {
      r.checked = r.value === S.layout;
    });
    qa('input[name="tlb-font"]').forEach(function (r) {
      r.checked = r.value === S.font;
    });
    syncColours();
    renderFields();
  }

  function codeError(msg) {
    var box = q("[data-tlb-code-error]");
    box.textContent = msg || "";
    box.hidden = !msg;
  }

  function setReorder(design) {
    S.reorder = design;
    root.removeAttribute("data-done");
    var box = q("[data-tlb-reorder-box]");
    if (box) box.open = true;
    var props = design.properties || {};
    q("[data-tlb-reorder-find]").hidden = true;
    q("[data-tlb-reorder-found]").hidden = false;
    q("[data-tlb-reorder-name]").textContent = props.Heading || props.Artwork || "your design";
    q("[data-tlb-reorder-code]").textContent = design.code;
    var img = q("[data-tlb-reorder-img]");
    var note = q("[data-tlb-reorder-art]");
    if (props._proof) {
      img.src = props._proof;
      img.alt = "Your saved label design" + (props.Heading ? ": " + props.Heading : "");
      img.hidden = false;
      note.hidden = true;
    } else {
      img.hidden = true;
      note.hidden = false;
    }
    var canEdit = !!(props._spec && props._proof);
    q("[data-tlb-reorder-edit]").hidden = !canEdit;
    if (design.packCount) {
      var radio = q('input[name="tlb-pack"][data-count="' + design.packCount + '"]');
      if (radio && !radio.disabled) radio.checked = true;
    }
    clearError();
    applyVisibility();
    updateSummary();
    var btn = q("[data-tlb-add]");
    btn.hidden = false;
    q("[data-tlb-done]").hidden = true;
  }

  function clearReorder() {
    S.reorder = null;
    q("[data-tlb-reorder-find]").hidden = false;
    q("[data-tlb-reorder-found]").hidden = true;
    q("#tlb-code").value = "";
    codeError("");
    applyVisibility();
    updateSummary();
    schedule();
  }

  function findDesign(code) {
    codeError("");
    var btn = q("[data-tlb-code-find]");
    btn.disabled = true;
    btn.textContent = "Looking...";
    return postJson(cfg.endpoint + "/lookup", { code: code })
      .then(function (res) {
        setReorder(res.design);
        return true;
      })
      .catch(function (e) {
        codeError(e && e.message ? e.message : "We could not look that up.");
        return false;
      })
      .then(function (ok) {
        btn.disabled = false;
        btn.textContent = "Find my design";
        return ok;
      });
  }

  function rawFromSpec(spec) {
    var colours = spec.colours || [];
    var logo = spec.logo || [];
    return {
      layout: spec.layout,
      heading: spec.heading,
      sub: spec.sub,
      caps: spec.caps,
      font: spec.font,
      accent: colours[0],
      text: colours[1],
      bg: colours[2],
      logoSide: logo[0],
      logoScale: logo[1],
      logoPanel: logo[2],
      logoFree: logo[3] ? { x: logo[4], y: logo[5], w: logo[6] } : null,
      headH: (spec.style || {}).hh,
      sideW: (spec.style || {}).sw,
      titleScale: (spec.style || {}).ts,
      align: (spec.style || {}).al,
      lineW: (spec.style || {}).lw,
      fields: (spec.fields || []).map(function (f) {
        return { caption: String(f[0] || ""), span: f[1], h: typeof f[2] === "number" ? f[2] : f[2] ? 1 : 0, on: !!f[3] };
      }),
    };
  }

  function fileNameFromUrl(url, fallback) {
    var name = decodeURIComponent((url.split("?")[0].split("/").pop() || "").trim());
    return name || fallback;
  }

  /* Bring the saved design back into the editor as a new design (setup fee applies). */
  function editReorder() {
    var r = S.reorder;
    var props = (r && r.properties) || {};
    var spec;
    try {
      spec = JSON.parse(props._spec || "null");
    } catch (e) {
      spec = null;
    }
    if (!spec) return;
    applyRaw(rawFromSpec(spec));
    S.mode = "design";
    S.designId = newDesignId();
    S.notes = "";
    if (S.logo) URL.revokeObjectURL(S.logo.url);
    S.logo = null;
    hist.cur = null;
    clearReorder();
    qa('input[name="tlb-mode"]').forEach(function (m) {
      m.checked = m.value === "design";
    });
    setMode("design");
    syncControls();
    if (spec.logo && props._logo_file) {
      fetch(props._logo_file)
        .then(function (res) {
          if (!res.ok) throw new Error("logo");
          return res.blob();
        })
        .then(function (blob) {
          var name = fileNameFromUrl(props._logo_file, "logo.png");
          name = name.replace(/^label-[a-z0-9]+-logo-/, "");
          loadLogo(new File([blob], name, { type: blob.type || "" }));
        })
        .catch(function () {
          showError("Your design is back, but we could not bring your logo across. Add it again under Text and logo.");
        });
    }
    var stage = q(".tlb-stage");
    if (stage && stage.scrollIntoView) stage.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /* --------------------------------------------------------------- wiring */

  function bindInputs() {
    qa("[data-tlb-input]").forEach(function (el) {
      var key = el.getAttribute("data-tlb-input");
      if (el.type === "checkbox") {
        el.checked = !!S[key];
        el.addEventListener("change", function () {
          S[key] = el.checked;
          schedule();
        });
      } else if (el.type === "radio") {
        el.checked = el.value === S[key];
        el.addEventListener("change", function () {
          if (el.checked) {
            S[key] = el.value;
            schedule();
          }
        });
      } else if (el.type === "range") {
        el.value = String(S[key]);
        el.addEventListener("input", function () {
          S[key] = Number(el.value);
          schedule();
        });
      } else {
        el.value = S[key] || "";
        el.addEventListener("input", function () {
          S[key] = el.value;
          schedule();
        });
      }
    });

    qa('input[name="tlb-mode"]').forEach(function (r) {
      r.addEventListener("change", function () {
        if (r.checked) setMode(r.value);
      });
    });
    qa('input[name="tlb-pack"]').forEach(function (r) {
      r.addEventListener("change", schedule);
    });
    q("[data-tlb-logo-file]").addEventListener("change", function (e) {
      var f = e.target.files && e.target.files[0];
      e.target.value = "";
      if (f) loadLogo(f);
    });
    q("[data-tlb-logo-remove]").addEventListener("click", function () {
      if (S.logo) URL.revokeObjectURL(S.logo.url);
      S.logo = null;
      S.logoFree = null;
      schedule();
    });
    q("[data-tlb-art-file]").addEventListener("change", function (e) {
      addOwnFiles(e.target.files || []);
      e.target.value = "";
    });
    q("[data-tlb-guides]").addEventListener("change", function (e) {
      S.guides = e.target.checked;
      schedule();
    });
    q("[data-tlb-add]").addEventListener("click", addToBasket);
    q("[data-tlb-quick-add]").addEventListener("click", addToBasket);
    q("[data-tlb-code-find]").addEventListener("click", function () {
      var code = q("#tlb-code").value.trim();
      if (!code) {
        codeError("Enter the reorder code from your order confirmation.");
        return;
      }
      findDesign(code);
    });
    q("#tlb-code").addEventListener("keydown", function (e) {
      if (e.key === "Enter") {
        e.preventDefault();
        q("[data-tlb-code-find]").click();
      }
    });
    q("[data-tlb-reorder-edit]").addEventListener("click", editReorder);
    q("[data-tlb-reorder-clear]").addEventListener("click", clearReorder);
    q("[data-tlb-another]").addEventListener("click", function () {
      S.designId = newDesignId();
      root.removeAttribute("data-done");
      if (S.reorder) clearReorder();
      q("[data-tlb-done]").hidden = true;
      q("[data-tlb-add]").hidden = false;
      var stage = q(".tlb-stage");
      if (stage && stage.scrollIntoView) stage.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function trackHeaderOffset() {
    var header = document.querySelector("[data-site-header-sticky]");
    if (!header) return;
    var pending = false;
    function update() {
      pending = false;
      var bottom = header.getBoundingClientRect().bottom;
      var top = window.innerWidth < 980 ? Math.max(0, Math.round(bottom)) : Math.max(0, Math.round(bottom)) + 8;
      root.style.setProperty("--tlb-top", top + "px");
    }
    function request() {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(update);
    }
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request);
    update();
  }

  restore();
  try {
    if (!new URLSearchParams(window.location.search).get("reorder")) restoreLogo();
  } catch (e) {
    restoreLogo();
  }
  buildPresets();
  buildLayouts();
  buildFonts();
  buildColours();
  buildFieldEvents();
  renderFields();
  bindInputs();
  bindDrag();
  syncColours();
  setMode("design");
  trackHeaderOffset();
  loadFonts().then(schedule);
  if (document.fonts && document.fonts.addEventListener) {
    document.fonts.addEventListener("loadingdone", schedule);
  }
  schedule();
  try {
    var fromUrl = new URLSearchParams(window.location.search).get("reorder");
    if (fromUrl) {
      q("#tlb-code").value = fromUrl;
      q("[data-tlb-reorder-box]").open = true;
      findDesign(fromUrl);
    }
  } catch (e) {
    /* no URLSearchParams: the code box still works */
  }
})();
